export default ({ IDL }) => {
  const Fee = IDL.Record({ 'fee' : IDL.Nat16, 'feeRecipient' : IDL.Principal });
  const Collection = IDL.Record({
    'fee' : Fee,
    '_principal' : IDL.Principal,
    'enabled' : IDL.Bool,
  });
  const Index = IDL.Nat;
  const CollectionItem = IDL.Record({
    'collectionPrincipal' : IDL.Principal,
    'identifier' : IDL.Text,
  });
  const Listing = IDL.Record({
    'ownerWhenListed' : IDL.Principal,
    'seller' : IDL.Principal,
    'listingPrice' : IDL.Nat,
    'enabled' : IDL.Bool,
    'updatedAt' : IDL.Int,
    'paymentMethodPrincipal' : IDL.Principal,
    'collectionItem' : CollectionItem,
  });
  const PaymentMethod = IDL.Record({
    'kind' : IDL.Variant({
      'cycles' : IDL.Null,
      'icpStandardKind' : IDL.Null,
      'origynFTv1StandardKind' : IDL.Null,
    }),
    '_principal' : IDL.Principal,
    'enabled' : IDL.Bool,
  });
  const Order = IDL.Record({
    'id' : IDL.Nat64,
    'status' : IDL.Variant({
      'created' : IDL.Null,
      'executed' : IDL.Null,
      'processing' : IDL.Null,
      'failed' : IDL.Null,
    }),
    'listing' : Listing,
    'expiresAt' : IDL.Int,
    'info' : IDL.Text,
    'recipient' : IDL.Principal,
    'initiatedAt' : IDL.Int,
    'initiatedBy' : IDL.Principal,
  });
  const ICPTs_2 = IDL.Record({ 'e8s' : IDL.Nat64 });
  const Payout = IDL.Record({
    'listing' : Listing,
    'what' : IDL.Variant({
      'icpSwap' : IDL.Record({
        'to' : IDL.Text,
        'from' : IDL.Principal,
        'amount' : ICPTs_2,
        'block_height' : IDL.Nat64,
      }),
      'cyclesSwap' : IDL.Record({
        'to' : IDL.Principal,
        'from' : IDL.Principal,
        'amount' : IDL.Nat,
      }),
      'fiatSwap' : IDL.Record({
        'to' : IDL.Principal,
        'from' : IDL.Principal,
        'transactionIndex' : IDL.Nat,
        'amount' : IDL.Nat,
      }),
      'collectionItemSwap' : IDL.Record({
        'to' : IDL.Principal,
        'from' : IDL.Principal,
        'transactionIndex' : IDL.Nat,
        'collectionItem' : CollectionItem,
      }),
    }),
    'recipient' : IDL.Principal,
  });
  const EventKind = IDL.Variant({
    'addPaymentMethod' : IDL.Record({
      'paymentMethod' : PaymentMethod,
      'caller' : IDL.Principal,
    }),
    'orderUpdated' : IDL.Record({
      'order' : Order,
      'errors' : IDL.Vec(IDL.Text),
      'caller' : IDL.Principal,
      'payouts' : IDL.Vec(Payout),
    }),
    'initializationKind' : IDL.Record({
      'admin' : IDL.Principal,
      'name' : IDL.Text,
      'caller' : IDL.Principal,
    }),
    'addListing' : IDL.Record({
      'listing' : Listing,
      'caller' : IDL.Principal,
    }),
    'updateCollection' : IDL.Record({
      'collection' : Collection,
      'caller' : IDL.Principal,
    }),
    'updateSettingsKind' : IDL.Record({
      'name' : IDL.Text,
      'caller' : IDL.Principal,
    }),
    'creationKind' : IDL.Record({ 'deployer' : IDL.Principal }),
    'orderCreated' : IDL.Record({ 'order' : Order, 'caller' : IDL.Principal }),
    'addCollection' : IDL.Record({
      'collection' : Collection,
      'caller' : IDL.Principal,
    }),
    'updatePaymentMethod' : IDL.Record({
      'paymentMethod' : PaymentMethod,
      'caller' : IDL.Principal,
    }),
    'updateAdminKind' : IDL.Record({
      'admin' : IDL.Principal,
      'caller' : IDL.Principal,
    }),
    'updateListing' : IDL.Record({
      'listing' : Listing,
      'caller' : IDL.Principal,
    }),
    'removeListing' : IDL.Record({
      'itemIdentifier' : IDL.Text,
      'collectionPrincipal' : IDL.Principal,
      'caller' : IDL.Principal,
    }),
  });
  const Event = IDL.Record({
    'time' : IDL.Int,
    'index' : Index,
    'eventKind' : EventKind,
  });
  const SubAccount = IDL.Vec(IDL.Nat8);
  const ICPTs = IDL.Record({ 'e8s' : IDL.Nat64 });
  const TransactionNotification = IDL.Record({
    'to' : IDL.Principal,
    'to_subaccount' : IDL.Opt(SubAccount),
    'from' : IDL.Principal,
    'memo' : IDL.Nat64,
    'from_subaccount' : IDL.Opt(SubAccount),
    'amount' : ICPTs,
    'block_height' : IDL.Nat64,
  });
  const MultiAssetsMarketplace = IDL.Service({
    'GLOBAL_LOCK' : IDL.Func([], [IDL.Bool], ['query']),
    'addCollection' : IDL.Func([Collection], [Index], []),
    'addListing' : IDL.Func([Listing], [Index], []),
    'addPaymentMethod' : IDL.Func([PaymentMethod], [Index], []),
    'admin' : IDL.Func([], [IDL.Principal], ['query']),
    'canisterBalance' : IDL.Func([], [IDL.Nat], ['query']),
    'createOrder' : IDL.Func(
        [CollectionItem, IDL.Principal, IDL.Int],
        [Order],
        [],
      ),
    'getClaimableIcpts' : IDL.Func(
        [],
        [IDL.Vec(IDL.Tuple(IDL.Principal, ICPTs_2))],
        [],
      ),
    'getCollections' : IDL.Func([], [IDL.Vec(Collection)], ['query']),
    'getEventRangeIndex' : IDL.Func(
        [Index, Index],
        [IDL.Vec(IDL.Opt(Event))],
        ['query'],
      ),
    'getIndex' : IDL.Func([], [Index], ['query']),
    'getListingByCollectionAndTokenID' : IDL.Func(
        [IDL.Principal, IDL.Text],
        [Listing],
        ['query'],
      ),
    'getListingsForCollection' : IDL.Func(
        [IDL.Principal],
        [IDL.Vec(Listing)],
        ['query'],
      ),
    'getPaymentMethods' : IDL.Func([], [IDL.Vec(PaymentMethod)], ['query']),
    'get_orders' : IDL.Func(
        [IDL.Nat64, IDL.Nat64],
        [IDL.Vec(IDL.Tuple(IDL.Nat64, IDL.Opt(Order)))],
        ['query'],
      ),
    'get_orders_count' : IDL.Func([], [IDL.Nat64], ['query']),
    'initialize' : IDL.Func([IDL.Principal, IDL.Text], [], []),
    'isInitialized' : IDL.Func([], [IDL.Bool], ['query']),
    'name' : IDL.Func([], [IDL.Text], ['query']),
    'removeListing' : IDL.Func([IDL.Principal, IDL.Text], [Index], []),
    'send_icpts' : IDL.Func([IDL.Text, IDL.Nat64, IDL.Nat64], [IDL.Nat64], []),
    'topUp' : IDL.Func([], [IDL.Nat], []),
    'transaction_notification' : IDL.Func([TransactionNotification], [], []),
    'updateAdmin' : IDL.Func([IDL.Principal], [], []),
    'updateCollection' : IDL.Func([Collection], [Index], []),
    'updateGlobalLock' : IDL.Func([IDL.Bool], [], []),
    'updateListing' : IDL.Func([Listing], [Index], []),
    'updatePaymentMethod' : IDL.Func([PaymentMethod], [Index], []),
    'updateSettings' : IDL.Func([IDL.Text], [Index], []),
    'whoami' : IDL.Func([], [IDL.Principal], []),
  });
  return MultiAssetsMarketplace;
};
export const init = ({ IDL }) => { return []; };